import { useCallback, useEffect, useState } from 'react';

import {
  findReportSeriesOfGroup,
  findShownCandidate,
  fromDisplaySets,
  groupKeyToResolveIn,
} from '../../AZmed/Data/reportSeries';
import {
  fetchReport,
  isDiskOnlyReportSource,
  peekReport,
  readCachedReport,
  revalidate,
} from './reportCache';

const NOTHING_LOADED = { uid: Symbol('nothing'), envelope: null, error: null };

/**
 * Loads the report of what the user is actually looking at — the report of the group
 * the active viewport's series belongs to, see groupKeyToResolveIn — from the cache
 * when it has it and from Orthanc when the profile is allowed to ask, then checks the
 * cached copy against the server in the background. Nothing outside that group can
 * ever be answered with, and an absence is reported as one.
 *
 * @param {Object} study The active study, as resolved by the panel
 * @param {string} [activeDisplaySetInstanceUID] The display set the active viewport
 *   shows, under the id the viewport itself carries. Absent until the viewport grid
 *   is filled, which is a resolving state and not an absence of report
 * @returns {Object} `report`, `isLoading`, `error` and `retry` to attempt again
 *   after a failure
 */
export const useStudyReport = (study, activeDisplaySetInstanceUID) => {
  // The outcome carries the key it belongs to. Plain report state would survive a
  // study change and show the previous patient's report under the new patient's
  // name for the length of two round trips.
  const [loaded, setLoaded] = useState(NOTHING_LOADED);

  // The effect only reruns on a new key, so an error would otherwise be final:
  // bringing the server back would leave the panel on its failure message until
  // the user visits another study and comes back.
  const [retryToken, setRetryToken] = useState(0);
  const retry = useCallback(() => {
    // Clearing the outcome too, otherwise the failure message would stay put
    // until the new attempt resolves and the button would look inert.
    setLoaded(NOTHING_LOADED);
    setRetryToken(token => token + 1);
  }, []);

  const studyInstanceUID = study && study.StudyInstanceUID;

  // The display sets of the study, however many arrive. Coming from the study list,
  // ViewerRetrieveStudyData narrows them to the series StudyListRoute stored when the
  // card was clicked; coming from a URL, or after a reload — that selection lives in
  // memory only — nothing is narrowed, and the same study then hands over every
  // language and every reprocessed version it holds at once.
  //
  // Read plainly during render rather than memoised: _addSeriesToStudy reassigns
  // study.displaySets on every arriving series, and each of those arrivals rerenders
  // through forceRerender, so reading the field on the spot always sees the current
  // one — where a memo would need that reassignment among its dependencies to avoid
  // staying pinned to a partial study.
  const displaySets = (study && study.displaySets) || null;

  const candidates = fromDisplaySets(displaySets);

  // What is on screen, which is the only thing a report shown next to it may
  // describe. Taken from the viewport rather than from the region filter: a region
  // knows a language and a certification and nothing else, so it cannot tell two
  // groups of one same language apart — the 1.2 and the 1.3 of a reprocessed study —
  // and it would then hand the older document to a reader of the newer images. The
  // viewport knows, and it knows on every entry path alike: from the study list, from
  // a bare URL, and after a reload.
  const shown = findShownCandidate(candidates, activeDisplaySetInstanceUID);

  // The group of the series on screen, or — for an acquisition series, which belongs
  // to no group and is what the viewer opens on — the study's single group when it has
  // exactly one. Null past that, and the rule lives in reportSeries next to the
  // grouping it reasons about rather than here: this hook is about when an outcome is
  // known, and that one is about which document a screen designates.
  const groupKey = groupKeyToResolveIn(candidates, shown);

  const reportSeries = findReportSeriesOfGroup(candidates, groupKey);
  const seriesInstanceUID = reportSeries && reportSeries.SeriesInstanceUID;

  // Not knowing yet is not "there is none", and the panel is mounted from the moment
  // the viewer is. Two distinct waits, and both of them always end:
  //   - no study, or no display set list at all: `studies` starts empty while the
  //     metadata is being fetched, so this is the commonest resolving state, not an
  //     absence — leaving it out made the panel deny a report on every viewer open.
  //     Null against empty is the whole test there, and no length test: processStudies
  //     fills one display set per QIDO series, placeholders for the not yet loaded
  //     ones included, before the very first render that carries a study.
  //   - display sets, but none of them on screen: no viewport carries a display set
  //     until ViewerMain fills the grid, which it does as soon as the display sets
  //     land, so the wait closes on a following render. It covers the mount, and the
  //     render or two of a study round trip where the viewport still points into the
  //     study being left.
  // A list that is there and empty, on the other hand, is a settled answer rather
  // than a wait: nothing can be displayed out of it, so nothing on screen can carry a
  // report, and waiting on a viewport that will never be filled would spin for good.
  const isResolving =
    !studyInstanceUID || !displaySets || (displaySets.length > 0 && !shown);

  // One key for the whole outcome: the series when one carries the report, the
  // study when none does. That case still needs a key, and a conclusion under it,
  // or the panel could not tell "there is no report" from "we do not know yet".
  //
  // Showing a series of another group moves this key on its own, since another group
  // resolves another series — which is what makes the displayed outcome follow the
  // viewport without a reload.
  const activeKey = seriesInstanceUID || studyInstanceUID;

  useEffect(() => {
    if (!activeKey || isResolving) return undefined;

    // Guarding the state writes rather than the requests: the network work is
    // shared between callers, so it must not be tied to one caller's lifetime.
    let cancelled = false;

    const load = async () => {
      // Either no group could be settled on for what is displayed, or its group
      // holds no series carrying a report: either way there is none to show here. A
      // report belongs to the series it hangs from, and answering with the one of
      // another group would put an English document in front of a German reader, or
      // the previous version of the algorithm next to the images of the current one —
      // in a medical context, neither is acceptable.
      if (!seriesInstanceUID) {
        // A conclusion, not a silence: stamped with the key so the panel reads
        // it as "no report" instead of staying on the placeholder for good.
        setLoaded({ uid: activeKey, envelope: null, error: null });

        return;
      }

      const cached = await readCachedReport(seriesInstanceUID);
      if (cancelled) return;

      if (cached) {
        setLoaded({ uid: activeKey, envelope: cached, error: null });

        const checked = await revalidate(cached);
        if (cancelled) return;

        setLoaded({ uid: activeKey, envelope: checked, error: null });

        return;
      }

      // A duplicated profile reads reports off its own disk and from nowhere else,
      // see isDiskOnlyReportSource. The cache having found nothing there is the whole
      // answer, so it is stamped under the key as a conclusion — the same shape as
      // the no-series case above, and for the same reason: an absence the panel can
      // state, never a wait it would hold the placeholder on.
      if (isDiskOnlyReportSource()) {
        setLoaded({ uid: activeKey, envelope: null, error: null });

        return;
      }

      const fetched = await fetchReport(seriesInstanceUID);
      if (cancelled) return;

      setLoaded({ uid: activeKey, envelope: fetched, error: null });
    };

    load().catch(error => {
      if (cancelled) return;

      console.error('[useStudyReport] Error loading the report:', error);
      setLoaded({ uid: activeKey, envelope: null, error });
    });

    return () => {
      cancelled = true;
    };
    // activeDisplaySetInstanceUID is listed although the body never reads it: it is
    // the input both keys above are derived from, and naming it keeps the series on
    // screen an acknowledged input of this load. A change that leaves both keys
    // untouched — moving between two series of one same group — reruns to the exact
    // same conclusion, served by the memory tier of the cache without a new request.
  }, [
    activeKey,
    seriesInstanceUID,
    studyInstanceUID,
    isResolving,
    activeDisplaySetInstanceUID,
    retryToken,
  ]);

  // Read during render, not in the effect: an effect runs after paint, so going
  // through it would flash the loading placeholder on every study round trip.
  //
  // Keyed by the resolved series and by nothing else. A study-keyed peek answers
  // through whichever series an earlier view resolved — the 1.2 one after a detour
  // through its card — and it answers under exactly the key that survives when the
  // group on screen carries no report series, which is precisely when the panel must
  // say there is none. So with no series there is nothing to peek at, and that is
  // the right answer rather than a missing one.
  const remembered = peekReport(seriesInstanceUID);
  const isCurrent = loaded.uid === activeKey;
  const envelope = isCurrent ? loaded.envelope : remembered;
  const error = isCurrent ? loaded.error : null;

  return {
    report: (envelope && envelope.report) || null,
    error,
    // Still loading as long as we have not concluded for this key, which covers
    // the study metadata landing, the fetch, and the offline lookup alike. No
    // `Boolean(activeKey)` term: without a key `isCurrent` is false against the
    // sentinel, so `!isCurrent` already carries it, and adding the guard back
    // "for symmetry" is exactly what made the panel conclude too early.
    isLoading: !envelope && !error && (isResolving || !isCurrent),
    retry,
  };
};
